import * as React from 'react';
import type { AvatarSize } from './Avatar';

/**
 * Props for the AvatarGroup component
 */
export interface AvatarGroupProps extends React.HTMLAttributes<HTMLFieldSetElement> {
  /**
   * Maximum number of avatars to display before collapsing
   * Remaining avatars are shown as a +N indicator
   */
  max?: number;
  /**
   * Size applied to all child avatars
   * @default 'md'
   */
  size?: AvatarSize;
  /**
   * Avatar components to display
   */
  children: React.ReactNode;
}

/**
 * Size configuration for overlap and remaining indicator
 */
const sizeConfig: Record<AvatarSize, { overlap: string; indicatorSize: string; text: string }> = {
  xs: {
    overlap: '-ml-2',       // 8px overlap
    indicatorSize: 'w-6 h-6', // 24px
    text: 'text-[10px]',
  },
  sm: {
    overlap: '-ml-2.5',     // 10px overlap
    indicatorSize: 'w-8 h-8', // 32px
    text: 'text-[11px]',
  },
  md: {
    overlap: '-ml-3',       // 12px overlap
    indicatorSize: 'w-10 h-10', // 40px
    text: 'text-[13px]',
  },
  lg: {
    overlap: '-ml-4',       // 16px overlap
    indicatorSize: 'w-12 h-12', // 48px
    text: 'text-sm',
  },
  xl: {
    overlap: '-ml-5',       // 20px overlap
    indicatorSize: 'w-16 h-16', // 64px
    text: 'text-base',
  },
};

/**
 * AvatarGroup component for displaying stacked avatars.
 *
 * Displays a row of overlapping avatars with optional max count.
 * When there are more avatars than max, shows a +N indicator.
 *
 * @example
 * // Basic usage
 * <AvatarGroup>
 *   <Avatar name="John" />
 *   <Avatar name="Jane" />
 *   <Avatar name="Bob" />
 * </AvatarGroup>
 *
 * @example
 * // With max limit
 * <AvatarGroup max={3}>
 *   <Avatar name="User 1" />
 *   <Avatar name="User 2" />
 *   <Avatar name="User 3" />
 *   <Avatar name="User 4" />
 *   <Avatar name="User 5" />
 * </AvatarGroup>
 *
 * @example
 * // Custom size
 * <AvatarGroup size="lg">
 *   <Avatar name="Alice" />
 *   <Avatar name="Bob" />
 * </AvatarGroup>
 */
const AvatarGroup = React.forwardRef<HTMLFieldSetElement, AvatarGroupProps>(
  (
    {
      max,
      size = 'md',
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    const config = sizeConfig[size];

    // Convert children to array and filter valid Avatar elements
    const childArray = React.Children.toArray(children);

    // Determine how many to show and how many are remaining
    const showCount = max !== undefined && max > 0 ? Math.min(max, childArray.length) : childArray.length;
    const remaining = childArray.length - showCount;
    const visibleChildren = childArray.slice(0, showCount);

    // Container classes
    const containerClasses = [
      'inline-flex items-center',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Remaining indicator classes
    const remainingClasses = [
      'inline-flex items-center justify-center',
      'rounded-full',
      'bg-primary-100',
      'text-primary-700',
      'font-medium',
      'ring-2 ring-white',
      config.indicatorSize,
      config.text,
      config.overlap,
    ].join(' ');

    return (
      <fieldset
        ref={ref}
        className={`${containerClasses} border-0 p-0 m-0 min-w-0`}
        aria-label={`Group of ${String(childArray.length)} avatars`}
        {...props}
      >
        {visibleChildren.map((child, index) => {
          // Clone each Avatar with bordered prop and size, applying overlap margin
          if (React.isValidElement(child)) {
            return (
              <div
                key={child.key}
                className={index > 0 ? config.overlap : ''}
                style={{ zIndex: visibleChildren.length - index }}
              >
                {React.cloneElement(child as React.ReactElement<{ bordered?: boolean; size?: AvatarSize }>, {
                  bordered: true,
                  size,
                })}
              </div>
            );
          }
          return child;
        })}

        {remaining > 0 && (
          <span
            className={remainingClasses}
            style={{ zIndex: 0 }}
            aria-label={`${String(remaining)} more`}
          >
            +{remaining}
          </span>
        )}
      </fieldset>
    );
  }
);

AvatarGroup.displayName = 'AvatarGroup';

export { AvatarGroup };
